import { Component, Input, OnInit } from '@angular/core';
import { AppSettingsService, hd, LoggerService } from '@nx-fe/services';
import { AttachmentService } from 'app/modules/ticket/services/attachment.service';
import { EmailActivityService } from 'app/modules/ticket/services/email.service';
import { catchError, map } from 'rxjs/operators';
import { ActivityTypeEnum, AttachmentActivity, InboundEmailActivity, UploadedFile } from '../../../ticket.model';
import { ActivityContextService } from '../activity/activity-context';

@Component({
  selector: 'xs-inbound-email-activity',
  templateUrl: './inbound-email-activity.component.html',
  styleUrls: ['./inbound-email-activity.component.scss'],
})
export class InboundEmailActivityComponent implements OnInit {
  @Input() activity: InboundEmailActivity;
  @Input() activityCtrl: ActivityContextService;

  // recipientsUserInfos: hd.IUserInfoModel[] = [];
  // recipientsCCUserInfos: hd.IUserInfoModel[] = [];

  errorMessage: string;
  isExpanded = false;
  hasMessageHistory = false;
  emailBody: string;
  emailWithoutHistory: string;
  attachments: AttachmentActivity[] = [];

  constructor(
    private $log: LoggerService,
    private emailService: EmailActivityService,
    private attachmentService: AttachmentService,
    private appSettingsService: AppSettingsService
  ) {}

  ngOnInit() {
    // if (this.activity.recipientIds) {
    //   const userInfos = this.userContextService.getUserInfoList(this.activity.recipientIds);
    //   userInfos.subscribe((userInfoList: hd.IUserInfoModel[]) => {
    //     this.recipientsUserInfos = userInfoList;
    //   });
    // }

    // if (this.activity.recipientCCIds) {
    //   const userInfos = this.userContextService.getUserInfoList(this.activity.recipientCCIds);
    //   userInfos.subscribe((userInfoList: hd.IUserInfoModel[]) => {
    //     this.recipientsCCUserInfos = userInfoList;
    //   });
    // }

    this.attachments = this.formatEmailAttachments(this.activity?.details?.files);

    const body = this.activity.details.body || '';
    if (this.activity.details.isHtml) {
      // Replace temporary inline attachment images with base64
      this.replaceInlineAttachments(body).then(e => {
        this.updateEmailBody(e);
      });
    } else {
      this.updateEmailBody(body);
    }
  }

  private updateEmailBody(body: string) {
    // To bring back unsanitised content:
    //   Pass the bodies through this.sanitizer.bypassSecurityTrustHtml (@angular/platform-browser/DomSanitizer)
    //   This gives you SafeHtml instead of string, but will be allowed on to the DOM
    // - Werner

    this.emailBody = body;
    this.hasMessageHistory = false;
    this.emailWithoutHistory = this.emailBody;

    // When the reply delimiter is available, check for email history
    this.emailService
      .getReplyDelimiter()
      .pipe(
        map(e => e.value),
        catchError(() => 'REPLY ABOVE THIS LINE')
      )
      .subscribe(delimiter => {
        const horizontalLineIndices = 380;
        const index = body.indexOf(delimiter);
        if (index !== -1) {
          this.hasMessageHistory = true;
          this.emailWithoutHistory = body.substring(0, index - horizontalLineIndices);
        }
      });
  }

  /** Replaces temporary inline image urls with base64 urls */
  private replaceInlineAttachments = async (html: string): Promise<string> => {
    const div = window.document.createElement('div');
    div.innerHTML = html;

    // HD-8834/8200 - Removing cellSpacing attribute as it causes unnecessary horizontal scrollbar
    const mailSeperatorElement = Array.from(div.querySelectorAll(`.MsoNormalTable[cellspacing='0']`));
    if (mailSeperatorElement.length > 0) {
      mailSeperatorElement.forEach(element => {
        element.removeAttribute('cellspacing');
      });
    }

    // Find all images with an 'activityid' attribute - these represent temporary attachment image urls
    const elements = div.getElementsByTagName('img');

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < elements.length; ++i) {
      const img = elements[i];
      if (img.hasAttribute('attachmentid')) {
        // Store the initial src and clear it immediately to prevent the browser from trying to fetch it
        const src = img.src;
        img.src = '';

        // TODO: Should we do this asynchronously?
        // Fetch base64 data for the temporary image src
        try {
          const attachmentId = img.getAttribute('attachmentid');
          const attachmentFile = this.activity.details.files?.find(
            file => file.fileId?.toUpperCase() === attachmentId?.toUpperCase()
          );
          if (attachmentFile) {
            const guid = await this.attachmentService
              .doCreateDownload({ attachmentId: attachmentFile.fileId, fileName: attachmentFile.fileName })
              .toPromise();
            const base64url = await this.attachmentService.fetchImageBase64(
              `${this.appSettingsService.attachmentServiceUrl}download/${guid}`
            );
            this.$log.debug('Replacing email inline attachment', attachmentId);
            img.src = base64url;
          } else {
            this.$log.error('Inbound email inline attachment is not part of attachments', attachmentId);
            img.src = src;
          }
        } catch (e) {
          // Revert to use the original image src
          this.$log.error(e);
          img.src = src;
        }
      }
    }

    return div.innerHTML;
  };

  toggleIsExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  private formatEmailAttachments(files: UploadedFile[]): AttachmentActivity[] {
    // TODO: Complete this logic so that we can actually preview/download these
    // It potentially clashes with outgoing email
    if (!files || files?.length === 0) {
      return [];
    }

    return files.map<AttachmentActivity>(file => {
      const minimum = {
        activityId: this.activity.activityId,
        activityTypeId: ActivityTypeEnum.attachment,
        creationDate: this.activity.creationDate,
        filename: file.fileName,
        details: {
          fileId: file.fileId,
          fileName: file.fileName,
          fileSize: file.fileSize,
        },
      };
      return (<unknown>minimum) as AttachmentActivity;
    });
  }
}
